
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string; // e.g., "max-w-xl", "max-w-2xl"
}

export const Modal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  maxWidth = "max-w-xl" 
}) => {
  // Prevent scrolling on the body when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Use createPortal to render the modal at the end of document.body
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6" role="dialog" aria-modal="true">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-slate-900/30 dark:bg-slate-900/50 transition-all"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal Container */}
      <div className={`relative w-full ${maxWidth} bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[90vh] animate-fade-in`}>
        {/* Header */}
        <div className="px-8 py-5 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between shrink-0">
           <h2 className="text-ds-h2 text-slate-800 dark:text-slate-100 truncate pr-4">
             {title}
           </h2>
           <button 
             onClick={onClose} 
             className="p-1.5 text-slate-300 hover:text-slate-500 dark:hover:text-slate-400 transition-colors"
             aria-label="Close"
           >
              <X size={18} strokeWidth={1.5} />
           </button>
        </div>
        
        {/* Scrollable Body */}
        <div className="px-8 py-6 overflow-y-auto custom-scrollbar">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};
